import React, { createContext, useReducer, useEffect } from "react"
import useLocalStorage from "../../hooks/useLocalStorage"
import { Moltin, client } from "../../puraearth/src/services/Moltin"
import { createCartIdentifier } from "@moltin/request"
import Cookies from "universal-cookie"

const EventEmitter = require("events")

const rateConsts = require("../../constants/currencyRateConsts")
const localeToCurrencyConsts = require("../../constants/localeCurrencyConsts")
const localeConsts = require("../../constants/localeConsts")
const initialState = {
  count: null,
  items: [],
  cartItems: [],
  promotionItems: [],
  taxItems: [],
  meta: null,
  subTotal: 0,
  shippingAmount: "000",
}

let shippingAddress, billingAddress, shippingAmount, shippingThreshold

const SET_CART = "SET_CART"
const RESET_CART = "RESET_CART"
let loaderActive = true

const cartReducer = (state, action) => {
  switch (action.type) {
    case SET_CART:
      let { data: items, meta } = action.payload
      const cartItems = items.filter(({ type }) => type === "cart_item")
      const promotionItems = items.filter(
        ({ type }) => type === "promotion_item",
      )
      const taxItems = items.filter(({ type }) => type === "tax_item")
      const count = cartItems.reduce(
        (sum, item) => item.type === "cart_item" && sum + item.quantity,
        0,
      )

      let subTotalValue = meta ? meta.display_price.without_tax.amount : 0
      let subTotal = meta ? meta.display_price.without_tax.formatted : 0
      let tax = meta ? meta.display_price.tax.formatted : 0
      let taxedSubtotal = meta ? meta.display_price.with_tax.formatted : 0
      let taxedSubtotalValue = meta ? meta.display_price.with_tax.amount : 0

      return {
        ...state,
        items,
        cartItems,
        promotionItems,
        taxItems,
        count,
        meta,
        subTotal,
        subTotalValue,
        tax,
        taxedSubtotal,
        taxedSubtotalValue,
      }

    case RESET_CART:
      return initialState

    default:
      return state
  }
}

const emitter = new EventEmitter()

const CartContext = createContext()
const { Provider, Consumer } = CartContext
let siteLocale
let siteCurrency

function CartProvider({
                        cartId: initialCartId = createCartIdentifier(),
                        ...props
                      }) {
  //TODO use built in SDK for storing cart ID
  const [cartId, setCartId] = useLocalStorage("mcart", initialCartId)
  let [cart, dispatch] = useReducer(cartReducer, initialState)
  if (typeof window !== "undefined") {
    const pathString = window.location.pathname
      .split("/")
      .filter(path => path !== "")

    siteLocale =
      typeof pathString[0] !== "undefined" ? pathString[0] : localeConsts.en

    siteCurrency =
      typeof pathString[0] !== "undefined"
        ? localeToCurrencyConsts[pathString[0]]
        : localeToCurrencyConsts[localeConsts.en]
  }

  let header = {
    "X-MOLTIN-CURRENCY": siteCurrency,
    "X-Moltin-Language": siteLocale,
  }

  async function checkCartCurrency(payload) {
    let cartContents = payload.data.filter(
      items => items !== null && items.type === "cart_item",
    )
    if (cartContents) {
      await client.delete(`carts/${cartId}`)
      await reAddItems(cartContents)
    }
  }

  async function getProductInventory(uuid) {
    await Moltin.Inventories.Get(uuid).then(inventories => {
      return inventories
    })
  }

  async function getCart(id) {
    setLoaderActive(true)

    let payload = await client.get(`carts/${id}/items`, header)
    dispatch({ type: SET_CART, payload })
    let cartCurrency = await client
      .get(`carts/${cartId}`)
      .then(payload => payload.data.meta.display_price.without_tax.currency)
    if (cartCurrency && siteCurrency) {
      if (siteCurrency !== cartCurrency) {
        await checkCartCurrency(payload)
      }
    }

    if (payload.data) {
      for (let i in payload.data) {
        let item = payload.data[i]
        if (item.sku !== "shipping" && item.type !== "promotion_item") {
          let product_id = item.product_id
          let inventory = await client.get(`inventories/${product_id}`, header)
          let product = await client.get(`products/${product_id}`, header)
          // Add correct, translated, name and description.
          payload.data[i].name = product.data.name
          payload.data[i].description = product.data.description
          payload.data[i].inventory = inventory
        }
      }
    }
    setLoaderActive(false)
  }

  useEffect(() => {
    let isSubscribed = true
    Moltin.Flows.GetEntries("shipping").then(result => {
      let { data } = result
      const shippingUSD = data[0]["shipping-USD"] || null
      const shippingCOP = data[0]["shipping-COP"] || null
      const shipThresholdUSD = data[0]["shipThresholdUSD"]
      const shipThresholdCOP = data[0]["shipThresholdCOP"]
      shippingAmount =
        siteLocale === localeConsts.en ? shippingUSD : shippingCOP
      shippingThreshold =
        siteLocale === localeConsts.en ? shipThresholdUSD : shipThresholdCOP
      getCart(cartId)
      setCartId(cartId)
    })
    return () => (isSubscribed = false)
  }, [cartId])

  async function addShipping() {
    const payload = await client
      .post(
        `carts/${cartId}/items`,
        {
          type: "custom_item",
          name: "Shipping",
          sku: "shipping",
          description: "Flat rate shipping cost",
          quantity: 1,
          price: {
            amount: shippingAmount,
          },
        },
        header,
      )
      .catch(error => {
        console.log(error)
      })
    dispatch({ type: SET_CART, payload })
  }

  async function addToCart(itemId, quantity, reAdd) {
    setLoaderActive(true)
    await client
      .post(
        `carts/${cartId}/items`,
        {
          type: "cart_item",
          id: itemId,
          quantity,
        },
        header,
      )
      .then(async payload => {
        const shippingItem = payload.data.filter(
          item => item.sku === "shipping",
        )
        const shippingId = shippingItem[0] ? shippingItem[0].id : null
        let price = shippingItem[0]
          ? payload.meta.display_price.without_tax.amount -
          shippingItem[0].meta.display_price.without_tax.value.amount
          : payload.meta.display_price.without_tax.amount
        if (price < shippingThreshold && shippingId === null) {
          await addShipping()
        }
        if (price >= shippingThreshold && shippingId !== null) {
          await removeFromCart(shippingId)
        }
        const { data: product } = await client.get(`products/${itemId}`, header)
        if (!reAdd) {
          emitter.emit("addedToCart", product, quantity)
        }
        for (let i in payload.data) {
          let item = payload.data[i]
          if (item.sku !== "shipping" && item.type !== "promotion_item" && !item.relationships) {
            const product = await client.get(
              `products/${item.product_id}`,
              header,
            )
            payload.data[i].name = product.data.name
            // await fetch(
            //   "https://55mdpi5wl2.execute-api.sa-east-1.amazonaws.com/prod/moltin_cart_calls",
            //   {
            //     method: "post",
            //     body: JSON.stringify({
            //       case: 0,
            //       cartId: cartId,
            //       itemId: item.id,
            //     }),
            //   }
            // ).catch(error => {
            //   console.error(error)
            // })
          }
        }
        dispatch({ type: SET_CART, payload })
        setLoaderActive(false)
      })
      .catch(error => {
        console.log(error)
      })
  }

  async function updateQuantity(itemId, quantity) {
    const payload = await client
      .put(
        `carts/${cartId}/items/${itemId}`,
        {
          headers: header,
          type: "cart_item",
          id: itemId,
          quantity: parseInt(quantity),
        },
        header,
      )
      .catch(console.error)
    dispatch({ type: SET_CART, payload })
    let shippingItem = payload.data.filter(item => item.sku === "shipping")
    const shippingId = shippingItem[0] ? shippingItem[0].id : null

    let price = shippingItem[0]
      ? payload.meta.display_price.without_tax.amount -
      shippingItem[0].meta.display_price.without_tax.value.amount
      : payload.meta.display_price.without_tax.amount

    if (price < shippingThreshold && shippingId === null) {
      await addShipping()
    }
    if (price >= shippingThreshold && shippingId !== null) {
      await removeFromCart(shippingId)
    }
  }

  async function removeFromCart(itemId) {
    const payload = await Moltin.Cart(cartId).RemoveItem(itemId)
    dispatch({ type: SET_CART, payload })
    let shippingItem = payload.data.filter(item => item.sku === "shipping")
    const shippingId = shippingItem[0] ? shippingItem[0].id : null

    let price = shippingItem[0]
      ? payload.meta.display_price.without_tax.amount -
      shippingItem[0].meta.display_price.without_tax.value.amount
      : payload.meta.display_price.without_tax.amount

    if (price < shippingThreshold && shippingId === null) {
      await addShipping()
    }
  }

  async function removeAllFromCart() {
    setLoaderActive(true)
    const cookies = new Cookies()
    if(cookies.get("used_promo") === "PURA10"){
      await updateCustomerToAddPromo()
    }
    await Moltin.Cart(cartId).Delete()
    dispatch({ type: RESET_CART })
  }

  async function updateCustomerToAddPromo() {
    const cookies = new Cookies()
    let xhr = new XMLHttpRequest()
    xhr.open("PUT", "https://ickr3dus78.execute-api.us-east-2.amazonaws.com/prod/customer", false)
    xhr.setRequestHeader("Content-Type", "application/json")
    xhr.onreadystatechange = async function() {
      if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
        const jsonData = JSON.parse(this.response)
        console.log(jsonData)
      }
    }
    const data = {
      customerId: cookies.get("customer_id"),
      promoCode: cookies.get("used_promo"),
      case: 0,
    }
    xhr.send(JSON.stringify(data))

  }

  async function addPromotion(promoCode) {
    let isCorrect = false
    await client
      .post(
        `carts/${cartId}/items`,
        {
          type: "promotion_item",
          code: promoCode,
        },
        header,
      )
      .then(async payload => {
        let items = payload.data
        let extraPromotionItems = []
        for (let i = 0; i < items.length; i++) {
          if (items[i].type === "promotion_item" && items[i].sku !== promoCode) {
            extraPromotionItems.push(items[i])
          }
        }
        for (let i = 0; i < extraPromotionItems.length; i++) {
          await removeFromCart(extraPromotionItems[i].id)
        }
        isCorrect = true
        dispatch({ type: SET_CART, payload })
      })
      .catch(error => {
        console.log(error)
      })
    return isCorrect
  }

  async function createOrder(
    customer,
    billingAddress,
    shippingAddress,
    langkey,
  ) {
    const data = {
      headers: header,
      customer: customer,
      billing_address: billingAddress,
      shipping_address: shippingAddress,
      language: langkey,
    }
    const payload = client
      .post(`carts/${cartId}/checkout`, data, header)
      .catch(error => {
        console.log(error)
        return new Promise((resolve, reject) => reject())
      })
    return payload
  }

  function getFormattedPrice(price) {
    const convertedPrice = new Intl.NumberFormat(localeConsts.en, {
      style: "currency",
      currency: siteCurrency,
    }).format(price)
    return convertedPrice
  }

  function getConvertedAmount(price) {
    let convertedPrice = price * rateConsts[siteCurrency]
    return convertedPrice
  }

  function setLoaderActive(status) {
    loaderActive = status
  }

  function getLoaderActive() {
    return loaderActive
  }

  async function reAddItems(items) {
    setLoaderActive(true)
    for (let x in items) {
      await addToCart(items[x].product_id, items[x].quantity, true)
    }
    setLoaderActive(false)
  }

  function getShippingAddress() {
    return shippingAddress
  }

  function getSavedBillingAddress() {
    return billingAddress
  }

  function setShippingAddress(newShippingAddress) {
    shippingAddress = newShippingAddress
  }

  function setSavedBillingAddress(newBillingAddress) {
    billingAddress = newBillingAddress
  }

  return (
    <Provider
      value={{
        ...cart,
        ...props,
        cartId,
        setLoaderActive,
        getLoaderActive,
        siteCurrency,
        addToCart,
        addShipping,
        updateQuantity,
        removeFromCart,
        removeAllFromCart,
        addPromotion,
        createOrder,
        getFormattedPrice,
        getConvertedAmount,
        getShippingAddress,
        getSavedBillingAddress,
        setShippingAddress,
        setSavedBillingAddress,
        getProductInventory,
        on: (...params) => {
          emitter.on(...params)
        },
        once: (...params) => {
          emitter.once(...params)
        },
        removePromotion: removeFromCart,
      }}
    >
      <div style={{ backgroundColor: "#FFF" }}>{props.children}</div>
    </Provider>
  )
}

export { CartProvider, CartContext, Consumer as CartConsumer }
